/**
 * RelaytrViews.ts - CRM View Definitions for Relaytr App
 * 
 * ARCHITECTURE:
 * All views belong in Viewr package, not in apps.
 * Apps consume views, they do NOT create them.
 * 
 * These views are consumed by Relaytr app via Interfacr.
 */

import type { ViewrView } from '../../types/ViewTypes';

export const relaytrViews: ViewrView[] = [
  // ============================================================================
  // CONTACTS VIEWS
  // ============================================================================
  
  {
    id: 'contact-list',
    name: 'Contact List',
    type: 'list',
    dataSource: 'contactr',
    category: 'crm',
    description: 'Tabular list of contacts with search and filtering',
    config: {
      columns: [
        { field: 'name', header: 'Name', sortable: true, width: 200 },
        { field: 'email', header: 'Email', sortable: true, width: 250 },
        { field: 'company', header: 'Company', sortable: true, width: 200 },
        { field: 'title', header: 'Title', sortable: true, width: 150 },
        { field: 'phone', header: 'Phone', sortable: false, width: 150 },
        { field: 'tags', header: 'Tags', sortable: false, width: 200, type: 'tags' },
      ],
      searchable: true,
      searchFields: ['name', 'email', 'company', 'title', 'notes'],
      filterable: true,
      sortable: true,
      pagination: {
        enabled: true,
        pageSize: 50,
        pageSizeOptions: [25, 50, 100, 200],
      },
      selection: {
        enabled: true,
        mode: 'multiple',
      },
      actions: [
        { id: 'add', label: 'Add Contact', icon: '➕', primary: true },
        { id: 'edit', label: 'Edit', icon: '✏️', requiresSelection: true },
        { id: 'delete', label: 'Delete', icon: '🗑️', requiresSelection: true },
        { id: 'export', label: 'Export', icon: '📤' },
      ],
    },
  },
  
  {
    id: 'contact-cards',
    name: 'Contact Cards',
    type: 'cards',
    dataSource: 'contactr',
    category: 'crm',
    description: 'Card-based grid view of contacts',
    config: {
      cardTemplate: 'contact-card',
      gridColumns: { xs: 1, sm: 2, md: 3, lg: 4 },
      gap: 16,
      searchable: true,
      searchFields: ['name', 'email', 'company'],
      filterable: true,
      sortOptions: [
        { field: 'name', label: 'Name' },
        { field: 'company', label: 'Company' },
        { field: 'lastContact', label: 'Last Contact' },
      ],
      cardFields: {
        avatar: 'avatar',
        title: 'name',
        subtitle: 'title',
        metadata: ['company', 'email', 'phone'],
        tags: 'tags',
        actions: ['view', 'edit', 'delete'],
      },
    },
  },
  
  {
    id: 'contact-detail',
    name: 'Contact Detail',
    type: 'detail',
    dataSource: 'contactr',
    category: 'crm',
    description: 'Detailed view of a single contact',
    config: {
      sections: [
        {
          id: 'basic',
          title: 'Basic Information',
          fields: ['name', 'email', 'phone', 'company', 'title'],
        },
        {
          id: 'address',
          title: 'Address',
          fields: ['street', 'city', 'state', 'zip', 'country'],
        },
        {
          id: 'social',
          title: 'Social Media',
          fields: ['linkedin', 'twitter', 'facebook'],
        },
        {
          id: 'notes',
          title: 'Notes',
          fields: ['notes'],
          type: 'textarea',
        },
        {
          id: 'tags',
          title: 'Tags',
          fields: ['tags'],
          type: 'tags',
        },
      ],
      actions: [
        { id: 'edit', label: 'Edit', icon: '✏️', primary: true },
        { id: 'delete', label: 'Delete', icon: '🗑️', confirm: true },
      ],
      relatedViews: [
        { viewId: 'activity-timeline', label: 'Activities', dataFilter: { contactId: '$id' } },
        { viewId: 'opportunity-list', label: 'Opportunities', dataFilter: { contactId: '$id' } },
      ],
    },
  },
  
  // ============================================================================
  // NETWORK VIEWS
  // ============================================================================
  
  {
    id: 'network-graph',
    name: 'Network Graph',
    type: 'graph',
    dataSource: 'groupr',
    category: 'crm',
    description: 'D3.js force-directed graph of contact relationships',
    config: {
      nodeType: 'contact',
      edgeType: 'relationship',
      layout: 'd3-force',
      nodeFields: {
        id: 'id',
        label: 'name',
        avatar: 'avatar',
        metadata: ['company', 'title'],
      },
      edgeFields: {
        source: 'fromId',
        target: 'toId',
        type: 'type',
        strength: 'strength',
      },
      visualization: {
        nodeSize: { min: 20, max: 60, field: 'connections' },
        nodeColor: { field: 'company', scheme: 'category10' },
        edgeWidth: { min: 1, max: 5, field: 'strength' },
        edgeColor: { field: 'type', scheme: 'pastel' },
      },
      interactions: {
        draggable: true,
        zoomable: true,
        clickToSelect: true,
        hoverToHighlight: true,
      },
      filters: [
        { field: 'type', label: 'Relationship Type', type: 'multiselect' },
        { field: 'strength', label: 'Connection Strength', type: 'range', min: 0, max: 1 },
      ],
    },
  },
  
  {
    id: 'network-insights',
    name: 'Network Insights',
    type: 'dashboard',
    dataSource: 'groupr',
    category: 'crm',
    description: 'Analytics dashboard for network metrics',
    config: {
      widgets: [
        {
          id: 'total-connections',
          type: 'metric',
          title: 'Total Connections',
          dataField: 'count',
          icon: '🕸️',
        },
        {
          id: 'strong-connections',
          type: 'metric',
          title: 'Strong Connections',
          dataField: 'strongCount',
          dataFilter: { strength: { $gte: 0.7 } },
          icon: '💪',
        },
        {
          id: 'collaboration-network',
          type: 'metric',
          title: 'Collaborations',
          dataField: 'count',
          dataFilter: { type: 'collaborates-with' },
          icon: '🤝',
        },
        {
          id: 'connection-distribution',
          type: 'chart',
          chartType: 'pie',
          title: 'Connection Types',
          dataField: 'type',
          aggregation: 'count',
        },
        {
          id: 'strength-distribution',
          type: 'chart',
          chartType: 'histogram',
          title: 'Connection Strength',
          dataField: 'strength',
          bins: 10,
        },
      ],
      layout: {
        columns: 3,
        gap: 16,
      },
    },
  },
  
  // ============================================================================
  // PIPELINE VIEWS
  // ============================================================================
  
  {
    id: 'pipeline-kanban',
    name: 'Pipeline Kanban',
    type: 'kanban',
    dataSource: 'organizr',
    category: 'crm',
    description: 'Kanban board for sales pipeline management',
    config: {
      columnsSource: 'stages',
      cardsSource: 'opportunities',
      columnFields: {
        id: 'id',
        title: 'name',
        order: 'order',
        color: 'color',
      },
      cardFields: {
        id: 'id',
        title: 'name',
        subtitle: 'contactId',
        metadata: ['value', 'probability', 'expectedCloseDate'],
        tags: 'tags',
      },
      draggable: true,
      onCardMove: 'updateStage',
      aggregations: [
        { field: 'value', type: 'sum', label: 'Total Value', format: 'currency' },
        { field: 'id', type: 'count', label: 'Count' },
      ],
      filters: [
        { field: 'tags', label: 'Tags', type: 'multiselect' },
        { field: 'probability', label: 'Probability', type: 'range', min: 0, max: 1 },
      ],
    },
  },
  
  {
    id: 'opportunity-list',
    name: 'Opportunity List',
    type: 'list',
    dataSource: 'organizr',
    category: 'crm',
    description: 'Tabular list of sales opportunities',
    config: {
      columns: [
        { field: 'name', header: 'Opportunity', sortable: true, width: 250 },
        { field: 'contactId', header: 'Contact', sortable: true, width: 200, type: 'lookup', lookupSource: 'contactr' },
        { field: 'stageId', header: 'Stage', sortable: true, width: 150, type: 'lookup', lookupSource: 'stages' },
        { field: 'value', header: 'Value', sortable: true, width: 120, type: 'currency' },
        { field: 'probability', header: 'Probability', sortable: true, width: 100, type: 'percentage' },
        { field: 'expectedCloseDate', header: 'Expected Close', sortable: true, width: 150, type: 'date' },
        { field: 'tags', header: 'Tags', sortable: false, width: 200, type: 'tags' },
      ],
      searchable: true,
      filterable: true,
      sortable: true,
      pagination: {
        enabled: true,
        pageSize: 50,
      },
    },
  },
  
  // ============================================================================
  // ACTIVITY VIEWS
  // ============================================================================
  
  {
    id: 'activity-timeline',
    name: 'Activity Timeline',
    type: 'timeline',
    dataSource: 'actr',
    category: 'crm',
    description: 'Chronological timeline of all activities',
    config: {
      groupBy: 'date',
      itemType: 'activity',
      itemFields: {
        id: 'id',
        title: 'subject',
        description: 'description',
        timestamp: 'timestamp',
        type: 'type',
        icon: 'type',
        metadata: ['contactId', 'userId'],
      },
      iconMapping: {
        email: '📧',
        call: '📞',
        meeting: '🤝',
        note: '📝',
        task: '✅',
      },
      filters: [
        { field: 'type', label: 'Activity Type', type: 'multiselect' },
        { field: 'contactId', label: 'Contact', type: 'select', source: 'contactr' },
        { field: 'timestamp', label: 'Date Range', type: 'daterange' },
      ],
      actions: [
        { id: 'add', label: 'Log Activity', icon: '➕', primary: true },
        { id: 'edit', label: 'Edit', icon: '✏️', requiresSelection: true },
        { id: 'delete', label: 'Delete', icon: '🗑️', requiresSelection: true },
      ],
    },
  },
  
  {
    id: 'activity-calendar',
    name: 'Activity Calendar',
    type: 'calendar',
    dataSource: 'actr',
    category: 'crm',
    description: 'Calendar view of scheduled activities',
    config: {
      eventSource: 'activities',
      eventFields: {
        id: 'id',
        title: 'subject',
        start: 'timestamp',
        end: 'endTime',
        type: 'type',
        color: 'type',
      },
      colorMapping: {
        email: '#3b82f6',
        call: '#10b981',
        meeting: '#f59e0b',
        note: '#6366f1',
        task: '#8b5cf6',
      },
      views: ['month', 'week', 'day', 'agenda'],
      defaultView: 'week',
      editable: true,
    },
  },
  
  // ============================================================================
  // REPORTS VIEWS
  // ============================================================================
  
  {
    id: 'reports-dashboard',
    name: 'CRM Dashboard',
    type: 'dashboard',
    dataSource: 'reportr',
    category: 'crm',
    description: 'Comprehensive CRM analytics dashboard',
    config: {
      widgets: [
        {
          id: 'total-contacts',
          type: 'metric',
          title: 'Total Contacts',
          dataSource: 'contactr',
          dataField: 'count',
          icon: '👥',
        },
        {
          id: 'total-connections',
          type: 'metric',
          title: 'Network Connections',
          dataSource: 'groupr',
          dataField: 'count',
          icon: '🕸️',
        },
        {
          id: 'pipeline-value',
          type: 'metric',
          title: 'Pipeline Value',
          dataSource: 'organizr',
          dataField: 'value',
          aggregation: 'sum',
          format: 'currency',
          icon: '📊',
        },
        {
          id: 'weighted-value',
          type: 'metric',
          title: 'Weighted Forecast',
          dataSource: 'organizr',
          dataField: 'weightedValue',
          aggregation: 'sum',
          format: 'currency',
          icon: '💰',
        },
        {
          id: 'total-activities',
          type: 'metric',
          title: 'Total Activities',
          dataSource: 'actr',
          dataField: 'count',
          icon: '📋',
        },
        {
          id: 'active-opportunities',
          type: 'metric',
          title: 'Active Opportunities',
          dataSource: 'organizr',
          dataField: 'count',
          icon: '🎯',
        },
        {
          id: 'pipeline-by-stage',
          type: 'chart',
          chartType: 'bar',
          title: 'Pipeline by Stage',
          dataSource: 'organizr',
          xField: 'stageName',
          yField: 'value',
          aggregation: 'sum',
        },
        {
          id: 'activity-trend',
          type: 'chart',
          chartType: 'line',
          title: 'Activity Trend',
          dataSource: 'actr',
          xField: 'date',
          yField: 'count',
          aggregation: 'count',
          groupBy: 'day',
        },
        {
          id: 'contacts-by-company',
          type: 'chart',
          chartType: 'pie',
          title: 'Contacts by Company',
          dataSource: 'contactr',
          field: 'company',
          aggregation: 'count',
          limit: 10,
        },
        {
          id: 'win-rate',
          type: 'chart',
          chartType: 'gauge',
          title: 'Win Rate',
          dataSource: 'organizr',
          calculation: 'wonCount / totalCount',
          format: 'percentage',
        },
      ],
      layout: {
        columns: 3,
        gap: 16,
        responsive: true,
      },
      filters: [
        { field: 'dateRange', label: 'Date Range', type: 'daterange', global: true },
        { field: 'tags', label: 'Tags', type: 'multiselect', global: true },
      ],
    },
  },
];

/**
 * Export view registry for Relaytr
 */
export const relaytrViewRegistry = {
  views: relaytrViews,
  categories: ['crm'],
  dataSources: ['contactr', 'groupr', 'organizr', 'actr', 'reportr'],
};
